import React, { useMemo, useState } from 'react';
import {
  Stack,
  IconButton,
  Tooltip,
  TextField,
  Alert,
  Box,
  FormGroup,
  FormControlLabel,
  Link as MuiLink,
  CircularProgress,
  Radio,
  Chip,
} from '@mui/material';
import { Button, Typography } from 'components/index';
import { Close, Delete, HelpOutline } from '@mui/icons-material';
import { FileUploadStatus, TypeOfFile } from './fileUpload.type';
import Dropzone from '../Dropzone';
import { toast } from 'react-toastify';
import { useQueryClient } from 'react-query';
import dataKeys from '../../common/react-query/data-keys';
import { uploadPayAppPDF } from './fileUpload.api';
import { handleError } from 'common/utils/util.errors';
import { preprocessPayappUploadResponse } from '../project/project.util';
import { FilesListItem, renderStatus } from './FileUploadDialog';
import { formatBytes } from '../../common/utils/common.util';
import { useNavigate } from 'react-router-dom';
import { MAX_PAYAPP_FILE_SIZE } from './Payapp.util';
import { ProjectLineItemsAndSummary } from '../project/project.type';
import { Division } from '../glossary/glossary.type';
import { GridRowId } from '@mui/x-data-grid';
import PayappProjectSelectionDialog from './PayappProjectSelectionDialog';
import { getBudgetandLineItems, retrieveProjectDraws } from '../project/project.api';
import { ProjectItem } from '../project/project.type';
import { ROUTES } from '../../common/constants/urls';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { splitPDF, isValidPageRange, getPDFPageCount, formatPageRangeForDisplay } from '../../common/utils/pdf.util';
import { payappStepIds } from '../../pages/PayappProcess';
import {
  CachedPayappEntry,
  getCachedPayappUploads,
  savePayappUploadToCache,
  removeCachedPayappUpload,
  relativeTime,
} from '../../common/utils/payappUploadCache.util';
import SEEDED_PAYAPPS from '../../dev/payappSeed';

export type DrawOption = { id: string; name: string; applicationNumber: number; isEnabled?: boolean };

/** Result passed to onUploadComplete when used in second-payapp mode */
export interface PayappUploadResult {
  parsedTable: any[];
  coverFields: any;
  sourceTargetHeadersMapping: Record<string, string>;
  fileUrl: string;
}

interface Props {
  onClose: () => void;
  filesToAccept: { [key: string]: string[] };
  projectId?: string;
  projectNumber?: string;
  projectName?: string;
  payappDrawId?: string;
  projectDraw?: ProjectLineItemsAndSummary;
  divisions?: Division[];
  /** When opening from a project row with draws pre-fetched, pass the draws so Step 1 shows project + draws without user selecting project. */
  initialDraws?: DrawOption[];
  setDialogTitle: (title: React.ReactNode) => void;
  setDialogActions: (actions: React.ReactNode) => void;
  context?: 'project' | 'global';
  /**
   * When provided (second-payapp mode), the result is passed to this callback
   * instead of navigating to /process-payapp.
   */
  onUploadComplete?: (result: PayappUploadResult) => void;
}

const PayAppUploadDialog: React.FC<Props> = ({
  onClose,
  filesToAccept,
  projectId,
  projectNumber,
  projectName,
  payappDrawId,
  projectDraw,
  divisions,
  initialDraws,
  setDialogTitle,
  setDialogActions,
  context,
  onUploadComplete,
}) => {
  const [fileItem, setFileItem] = useState<FilesListItem | null>(null);
  const [splitFileItem, setSplitFileItem] = useState<FilesListItem | null>(null);
  const [pageRange, setPageRange] = useState('');
  const [pdfPageCount, setPdfPageCount] = useState<number | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<ProjectItem | null>(null);
  const [selectedDrawId, setSelectedDrawId] = useState<string | null>(null);
  const [retrievedDraws, setRetrievedDraws] = useState<DrawOption[]>([]);
  const [isProjectSelectionDialogOpen, setIsProjectSelectionDialogOpen] = useState(false);
  const [isRetrievingDraws, setIsRetrievingDraws] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSplittingPDF, setIsSplittingPDF] = useState(false);
  const [cachedPayapps, setCachedPayapps] = useState<CachedPayappEntry[]>(() =>
    process.env.NODE_ENV !== 'production' || process.env.REACT_APP_ENV === 'test' ? getCachedPayappUploads() : [],
  );
  // In-memory only — never written to localStorage
  const seededPayapps = React.useMemo<CachedPayappEntry[]>(() => {
    if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_ENV !== 'test') return [];
    return SEEDED_PAYAPPS.map(s => ({
      id: s.id,
      displayName: s.displayName,
      savedAt: 0,
      state: {
        parsedTable: preprocessPayappUploadResponse(s.table),
        coverFields: s.coverFields,
        sourceTargetHeadersMapping: s.headerMappings || {},
      },
    }));
  }, []);
  const [loadingCachedId, setLoadingCachedId] = useState<string | null>(null);
  const pageRangeRef = React.useRef('');

  // When opened from project row with pre-fetched draws, initialize Step 1 state
  const hasInitialDraws = projectId != null && initialDraws !== undefined;
  React.useEffect(() => {
    if (hasInitialDraws) {
      setSelectedProjectId(projectId);
      setRetrievedDraws(Array.isArray(initialDraws) ? initialDraws : []);
      setSelectedProject({ id: projectId, projectNumber: projectNumber || '', projectTitle: projectName || '' } as ProjectItem);
      setSelectedDrawId(null);
    }
  }, [projectId, projectNumber, projectName, hasInitialDraws, initialDraws]);

  React.useEffect(() => {
    pageRangeRef.current = pageRange;
  }, [pageRange]);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const isPageRangeValid = React.useMemo(() => {
    return isValidPageRange(pageRange, pdfPageCount || undefined);
  }, [pageRange, pdfPageCount]);

  const formattedPageRange = React.useMemo(() => {
    return formatPageRangeForDisplay(pageRange);
  }, [pageRange]);

  const effectiveProjectId =
    context === 'project' && projectId ? projectId : hasInitialDraws && selectedProjectId ? projectId : selectedProjectId;
  const effectiveDrawId = context === 'project' && payappDrawId ? payappDrawId : selectedDrawId;
  const showStep1 = context === 'global' || hasInitialDraws;

  // Use split file if available, otherwise use original file
  const currentFileItem = splitFileItem || fileItem;

  const handleRemove = React.useCallback(() => {
    setFileItem(null);
    setSplitFileItem(null);
    setPdfPageCount(null);
  }, []);

  const handleCloseDialog = React.useCallback(() => {
    if (fileItem?.status === FileUploadStatus.Uploading) {
      toast.warn('Upload in progress. Please wait.');
      return;
    } else if (isSplittingPDF) {
      toast.warn('Splitting PDF in progress. Please wait.');
      return;
    }
    onClose();
    setPageRange('');
    setPdfPageCount(null);
    setTimeout(() => handleRemove(), 200);
  }, [fileItem?.status, isSplittingPDF, onClose, handleRemove]);

  const getButtonText = React.useCallback(
    (fileStatus: string, fallbackText: string) => {
      if (isSplittingPDF) return 'Splitting PDF...';
      if (fileStatus === FileUploadStatus.Uploading) return 'Uploading';
      if (fileStatus === FileUploadStatus.Parsing) return 'Parsing';
      return fallbackText;
    },
    [isSplittingPDF],
  );

  const uploadPayAppWithProgress = React.useCallback(
    async (fileToUpload: File, useSplitState?: boolean) => {
      if (!effectiveProjectId) {
        throw new Error('Project ID is missing');
      }
      return uploadPayAppPDF(
        fileToUpload,
        (name, status, errors, progress) => {
          // Update the current file item (split or original)
          if (useSplitState) {
            setSplitFileItem(prev => (prev ? { ...prev, status, errors, progress } : { file: fileToUpload, status, errors, progress }));
          } else {
            setFileItem(prev => (prev ? { ...prev, status, errors, progress } : { file: fileToUpload, status, errors, progress }));
          }
        },
        effectiveProjectId,
        effectiveDrawId || undefined, // Pass the drawId, convert null to undefined
      );
    },
    [effectiveProjectId, effectiveDrawId],
  );

  const [isManualUpdating, setIsManualUpdating] = React.useState(false);
  const [fetchedProjectDraw, setFetchedProjectDraw] = React.useState<ProjectLineItemsAndSummary | null>(null);

  // Fetch draw data when a draw is selected (for global context where projectDraw prop isn't passed)
  React.useEffect(() => {
    if (!effectiveProjectId || !effectiveDrawId || projectDraw) {
      setFetchedProjectDraw(null);
      return;
    }
    let cancelled = false;
    getBudgetandLineItems(effectiveProjectId, effectiveDrawId)
      .then(data => {
        if (!cancelled) setFetchedProjectDraw(data);
      })
      .catch(() => {
        if (!cancelled) setFetchedProjectDraw(null);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveProjectId, effectiveDrawId, projectDraw]);

  const effectiveProjectDraw = projectDraw ?? fetchedProjectDraw;

  const handleManualUpdate = React.useCallback(() => {
    if (!effectiveProjectDraw || !effectiveProjectId || !effectiveDrawId) return;

    setIsManualUpdating(true);
    try {
      const modifiedDraw = {
        ...effectiveProjectDraw,
        lineItems: (effectiveProjectDraw.lineItems || []).map((item: any) => {
          const newPrevApp = (item.previousApplication || 0) + (item.completeThisPeriod || 0) + (item.storedThisPeriod || 0);
          const baseline = {
            ...item,
            payappRow: undefined,
            projectRow: undefined,
            previousApplication: newPrevApp,
            completeThisPeriod: 0,
            storedThisPeriod: 0,
            cdccApproved: 0,
            completedStoredToDate: newPrevApp,
            balanceToComplete: (item.revisedBudget || 0) - newPrevApp,
          };
          return {
            ...baseline,
            payappRow: baseline,
            projectRow: { ...item, previousApplication: newPrevApp },
          };
        }),
      };

      onClose();
      navigate(`${ROUTES.PROJECTS}/${effectiveProjectId}${ROUTES.PROCESSPAYAPP}/${effectiveDrawId}/${payappStepIds[3]}`, {
        state: {
          isManualUpdate: true,
          projectId: effectiveProjectId,
          projectNumber: context === 'project' ? projectNumber : selectedProject?.projectNumber || '',
          projectName: context === 'project' ? projectName : selectedProject?.projectTitle || '',
          payappDrawId: effectiveDrawId,
          projectDraw: modifiedDraw,
          divisions: divisions || [],
        },
      });
    } catch {
      setIsManualUpdating(false);
    }
  }, [
    effectiveProjectDraw,
    effectiveProjectId,
    effectiveDrawId,
    context,
    projectNumber,
    projectName,
    selectedProject,
    divisions,
    onClose,
    navigate,
  ]);

  const handleSplitPDF = React.useCallback(async (): Promise<FilesListItem | null> => {
    if (!fileItem || !pageRange.trim()) return null;

    setIsSplittingPDF(true);
    try {
      const splitFile = await splitPDF(fileItem.file, pageRange);
      const splitFileListItem = {
        file: splitFile,
        status: FileUploadStatus.Idle,
        errors: [],
        progress: 0,
      };
      setSplitFileItem(splitFileListItem);
      toast.success(`PDF split successfully.`);
      return splitFileListItem;
    } catch (error) {
      toast.error('Failed to split PDF. Please check the page range and try again.');
      console.error('PDF splitting error:', error);
    } finally {
      setIsSplittingPDF(false);
    }
    return null;
  }, [fileItem, pageRange]);

  const handlePageRangeChange = React.useCallback(
    (newPageRange: string) => {
      setPageRange(newPageRange);
      // Clear split file when page range changes so user can re-split
      if (splitFileItem) {
        setSplitFileItem(null);
      }
    },
    [splitFileItem],
  );

  const handleRemoveCached = React.useCallback((id: string) => {
    removeCachedPayappUpload(id);
    setCachedPayapps(prev => prev.filter(e => e.id !== id));
  }, []);

  const handleUseCached = React.useCallback(
    async (entry: CachedPayappEntry) => {
      if (!effectiveProjectId || !effectiveDrawId) {
        toast.error('Please select a project and draw first.');
        return;
      }
      setLoadingCachedId(entry.id);
      try {
        const freshProjectDraw = await getBudgetandLineItems(effectiveProjectId, effectiveDrawId);
        if (!freshProjectDraw) {
          toast.error('Project draw not found.');
          return;
        }
        const effProjectNumber = context === 'project' ? (projectNumber ?? '') : (selectedProject?.projectNumber ?? '');
        const effProjectName = context === 'project' ? (projectName ?? '') : (selectedProject?.projectTitle ?? '');
        if (onUploadComplete) {
          onUploadComplete({
            parsedTable: entry.state.parsedTable,
            coverFields: entry.state.coverFields,
            sourceTargetHeadersMapping: entry.state.sourceTargetHeadersMapping,
            fileUrl: '',
          });
          handleCloseDialog();
        } else {
          navigate(`${ROUTES.PROJECTS}/${effectiveProjectId}${ROUTES.PROCESSPAYAPP}/${effectiveDrawId}/${payappStepIds[0]}`, {
            state: {
              parsedTable: entry.state.parsedTable,
              coverFields: entry.state.coverFields,
              sourceTargetHeadersMapping: entry.state.sourceTargetHeadersMapping,
              projectId: effectiveProjectId,
              projectNumber: effProjectNumber,
              projectName: effProjectName,
              payappDrawId: effectiveDrawId,
              projectDraw: freshProjectDraw,
              divisions: divisions ?? [],
              fileUrl: null,
            },
          });
          handleCloseDialog();
        }
      } catch (error) {
        handleError(error);
      } finally {
        setLoadingCachedId(null);
      }
    },
    [
      effectiveProjectId,
      effectiveDrawId,
      context,
      projectNumber,
      projectName,
      selectedProject,
      divisions,
      onUploadComplete,
      navigate,
      handleCloseDialog,
    ],
  );

  const handleUpload = React.useCallback(async () => {
    if (!currentFileItem || !effectiveProjectId) return;

    try {
      if (!effectiveProjectId || !effectiveDrawId) {
        toast.error('Project or draw not selected.');
        return;
      }

      let fileToUpload = currentFileItem.file;
      let useSplitState = Boolean(splitFileItem);

      // If page range is specified but PDF hasn't been split yet, split it first
      if (pageRange.trim() && !splitFileItem && fileItem) {
        const splitFileListItem = await handleSplitPDF();
        if (!splitFileListItem) return;
        fileToUpload = splitFileListItem.file;
        useSplitState = true;
      }

      if (fileToUpload.size > MAX_PAYAPP_FILE_SIZE) {
        toast.error(`File size exceeds the limit of ${MAX_PAYAPP_FILE_SIZE / 1024 / 1024} MB`);
        return;
      }

      setIsUploading(true);

      let result;
      if (!projectDraw) {
        const [fetchedProjectDraw, uploadResult] = await Promise.all([
          getBudgetandLineItems(effectiveProjectId, effectiveDrawId),
          uploadPayAppWithProgress(fileToUpload, useSplitState),
        ]);

        if (!fetchedProjectDraw) {
          toast.error('Project draw not found.');
          return;
        }
        projectDraw = fetchedProjectDraw;
        result = uploadResult;
      } else {
        result = await uploadPayAppWithProgress(fileToUpload, useSplitState);
      }

      const resultIsValid = Array.isArray(result.table) && result.table?.length > 0;
      if (!resultIsValid) {
        toast.error('Empty response from PayApp upload.');
        return;
      }
      const preprocessedRows = preprocessPayappUploadResponse(result.table || []);

      if (process.env.NODE_ENV !== 'production' || process.env.REACT_APP_ENV === 'test') {
        try {
          savePayappUploadToCache({
            id: fileToUpload.name,
            displayName: fileToUpload.name,
            savedAt: Date.now(),
            state: {
              parsedTable: preprocessedRows,
              coverFields: result.coverFields,
              sourceTargetHeadersMapping: result.headerMappings || {},
            },
          });
          setCachedPayapps(getCachedPayappUploads());
        } catch {
          // silently swallow cache errors
        }
      }

      // Create blob URL for the file (split or original)
      const fileUrl = URL.createObjectURL(fileToUpload);

      queryClient.invalidateQueries(dataKeys.files);

      if (onUploadComplete) {
        // Second-payapp mode: hand result back to caller instead of navigating
        onUploadComplete({
          parsedTable: preprocessedRows,
          coverFields: result.coverFields,
          sourceTargetHeadersMapping: result.headerMappings || {},
          fileUrl,
        });
        handleCloseDialog();
      } else {
        const stepId = payappStepIds[0];
        navigate(`${ROUTES.PROJECTS}/${effectiveProjectId}${ROUTES.PROCESSPAYAPP}/${effectiveDrawId}/${stepId}`, {
          state: {
            parsedTable: preprocessedRows,
            coverFields: result.coverFields,
            sourceTargetHeadersMapping: result.headerMappings,
            projectId: effectiveProjectId,
            projectNumber: context === 'project' ? projectNumber : selectedProject?.projectNumber || '',
            projectName: context === 'project' ? projectName : selectedProject?.projectTitle || '',
            payappDrawId: effectiveDrawId,
            projectDraw: projectDraw,
            divisions: divisions,
            fileUrl: fileUrl,
          },
        });
        handleCloseDialog();
      }
    } catch (error) {
      handleError(error);
    } finally {
      setIsUploading(false);
    }
  }, [
    currentFileItem,
    effectiveProjectId,
    effectiveDrawId,
    pageRange,
    splitFileItem,
    fileItem,
    projectDraw,
    divisions,
    uploadPayAppWithProgress,
    navigate,
    queryClient,
    handleCloseDialog,
    handleSplitPDF,
  ]);

  React.useEffect(() => {
    const dialogTitle = context === 'project' || hasInitialDraws ? 'Upload PayApp' : 'Upload File';
    setDialogTitle(dialogTitle);

    setDialogActions(
      <>
        <Button variant='outlined' color='secondary' onClick={handleCloseDialog}>
          Cancel
        </Button>
        <Button
          disabled={
            currentFileItem == null ||
            !isPageRangeValid ||
            currentFileItem.status !== FileUploadStatus.Idle ||
            (showStep1 && (!effectiveProjectId || !effectiveDrawId)) ||
            isUploading ||
            isSplittingPDF
          }
          onClick={handleUpload}
        >
          {getButtonText(currentFileItem?.status ?? '', pageRange.trim() && !splitFileItem ? 'Split & Upload' : 'Upload')}
        </Button>
      </>,
    );
  }, [
    currentFileItem,
    isPageRangeValid,
    pageRange,
    splitFileItem,
    showStep1,
    hasInitialDraws,
    effectiveProjectId,
    effectiveDrawId,
    isUploading,
    isSplittingPDF,
    context,
    setDialogTitle,
    setDialogActions,
    handleCloseDialog,
    handleUpload,
    getButtonText,
  ]);

  const enabledRetrievedDraws = useMemo(() => retrievedDraws.filter(d => d.isEnabled !== false), [retrievedDraws]);
  React.useEffect(() => {
    if (enabledRetrievedDraws.length === 1) {
      setSelectedDrawId(enabledRetrievedDraws[0].id);
    }
  }, [retrievedDraws]);

  const handleDrop = (files: File[]) => {
    if (files.length !== 1) {
      toast.error('Only one PayApp file can be uploaded at a time.', { autoClose: 2000 });
      return;
    }
    const fileListItem = { file: files[0], status: FileUploadStatus.Idle, errors: [], progress: 0 };
    setFileItem(fileListItem);
    setSplitFileItem(null); // Reset split file when new file is dropped
    setPageRange(''); // Reset page range when new file is dropped
    setPdfPageCount(null); // Reset page count when new file is dropped
  };

  const retreiveProjectDraws = async (projectId: readonly GridRowId[]) => {
    if (projectId.length === 0) {
      toast.error('Please select a project.');
      return;
    }

    setSelectedProjectId(projectId[0].toString());
    setIsRetrievingDraws(true);

    try {
      const response = await retrieveProjectDraws(projectId[0].toString());
      setRetrievedDraws(response?.draws || []); // expected array of draws
      setSelectedProject({
        id: projectId[0].toString(),
        projectNumber: response?.projectNumber,
        projectTitle: response?.projectTitle,
      } as ProjectItem);
      setIsProjectSelectionDialogOpen(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsRetrievingDraws(false);
    }
  };

  // Effect to get PDF page count when file is uploaded
  React.useEffect(() => {
    const fetchPageCount = async () => {
      if (fileItem?.file && fileItem.file.type === 'application/pdf') {
        try {
          const pageCount = await getPDFPageCount(fileItem.file);
          setPdfPageCount(pageCount);
        } catch (error) {
          console.error('Failed to get PDF page count:', error);
          setPdfPageCount(null);
        }
      } else {
        setPdfPageCount(null);
      }
    };

    fetchPageCount();
  }, [fileItem]);

  return (
    <Box>
      <Stack spacing={2}>
        {/* Show warning if PayApp draft exists */}
        {context === 'project' && projectDraw?.payappId && (
          <Alert severity='warning'>Previous Payapp draft will be discarded on upload</Alert>
        )}

        {/* Dev/test only: recently uploaded payapps */}
        {(process.env.NODE_ENV !== 'production' || process.env.REACT_APP_ENV === 'test') && cachedPayapps.length > 0 && (
          <Box sx={{ border: '1px dashed', borderColor: 'warning.main', borderRadius: 1, p: 1.5 }}>
            <Typography variant='caption' color='warning.dark' fontWeight='bold' display='block' mb={1}>
              RECENT PAYAPPS [DEV ONLY]
            </Typography>
            <Stack spacing={0.5}>
              {cachedPayapps.map(entry => (
                <Stack key={entry.id} direction='row' alignItems='center' spacing={1}>
                  <Button
                    variant='outlined'
                    size='small'
                    onClick={() => handleUseCached(entry)}
                    disabled={loadingCachedId !== null}
                    startIcon={loadingCachedId === entry.id ? <CircularProgress size={14} /> : undefined}
                    sx={{ flex: 1, justifyContent: 'flex-start', textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    {entry.displayName}
                  </Button>
                  <Typography variant='caption' color='text.secondary' sx={{ minWidth: 44, textAlign: 'right' }}>
                    {relativeTime(entry.savedAt)}
                  </Typography>
                  <IconButton size='small' onClick={() => handleRemoveCached(entry.id)} disabled={loadingCachedId !== null}>
                    <Close fontSize='small' />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        {/* Dev/test only: pre-seeded payapps from payappSeed.ts */}
        {(process.env.NODE_ENV !== 'production' || process.env.REACT_APP_ENV === 'test') && seededPayapps.length > 0 && (
          <Box sx={{ border: '1px dashed', borderColor: 'info.main', borderRadius: 1, p: 1.5 }}>
            <Typography variant='caption' color='info.dark' fontWeight='bold' display='block' mb={1}>
              PRE-SEEDED [DEV ONLY]
            </Typography>
            <Stack spacing={0.5}>
              {seededPayapps.map(entry => (
                <Stack key={entry.id} direction='row' alignItems='center' spacing={1}>
                  <Button
                    variant='outlined'
                    size='small'
                    onClick={() => handleUseCached(entry)}
                    disabled={loadingCachedId !== null}
                    startIcon={loadingCachedId === entry.id ? <CircularProgress size={14} /> : undefined}
                    sx={{ flex: 1, justifyContent: 'flex-start', textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    {entry.displayName}
                  </Button>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        {showStep1 && (
          <Box>
            <Typography variant='h6' mb={1}>
              Step 1: Select Project Draw
            </Typography>
            <Box p={2} border='2px dashed rgb(204, 204, 204)' sx={{ position: 'relative' }}>
              {!selectedProjectId ? (
                <Stack alignItems='center'>
                  <Button variant='contained' onClick={() => setIsProjectSelectionDialogOpen(true)}>
                    Select Project
                  </Button>
                </Stack>
              ) : isRetrievingDraws ? (
                <Stack alignItems='center' justifyContent='center' sx={{ mt: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant='caption' sx={{ mt: 1 }}>
                    Retrieving draws...
                  </Typography>
                </Stack>
              ) : (
                <>
                  <IconButton
                    size='small'
                    onClick={() => {
                      setSelectedProjectId(null);
                      setSelectedProject(null);
                      setSelectedDrawId(null);
                      setRetrievedDraws([]);
                    }}
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                  >
                    <Close fontSize='small' />
                  </IconButton>

                  <Stack spacing={0.5} alignItems='flex-start'>
                    <MuiLink
                      href={`${ROUTES.PROJECTS}/${selectedProjectId}`}
                      target='_blank'
                      rel='noopener noreferrer'
                      underline='hover'
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'primary.main' }}
                    >
                      <Typography variant='body1' component='span'>
                        {selectedProject?.projectNumber} - {selectedProject?.projectTitle}
                      </Typography>
                      <OpenInNewIcon fontSize='small' />
                    </MuiLink>

                    {!selectedDrawId && (
                      <Typography variant='caption' color='error'>
                        {enabledRetrievedDraws.length > 0 ? '* Select one of the following draws' : '* This project has no draws'}
                      </Typography>
                    )}
                  </Stack>

                  <FormGroup>
                    {enabledRetrievedDraws.map(draw => (
                      <FormControlLabel
                        key={draw?.id}
                        control={
                          <Radio
                            checked={selectedDrawId === draw.id}
                            onChange={() => setSelectedDrawId(draw.id)}
                            value={draw.id}
                            name='draw-selection'
                          />
                        }
                        label={draw.name}
                      />
                    ))}
                  </FormGroup>
                </>
              )}
            </Box>
          </Box>
        )}

        {showStep1 && (
          <Typography variant='h6' mt={3}>
            Step 2: Select PayApp
          </Typography>
        )}

        {!currentFileItem && <Dropzone onDrop={handleDrop} multiple={false} accept={filesToAccept} />}

        {!currentFileItem && !!effectiveProjectDraw && !onUploadComplete && (
          <Stack alignItems='center' spacing={1} sx={{ mt: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              — OR —
            </Typography>
            <Button
              variant='text'
              color='primary'
              onClick={handleManualUpdate}
              disabled={!effectiveProjectId || !effectiveDrawId || isManualUpdating}
              loading={isManualUpdating}
              sx={{ textTransform: 'none', fontSize: '0.95rem' }}
            >
              Manually Update PayApp
            </Button>
          </Stack>
        )}

        {currentFileItem && (
          <Box p={2} border='2px dashed rgb(204, 204, 204)' sx={{ position: 'relative' }}>
            <Stack direction='row' spacing={2} alignItems='center'>
              <Stack flexGrow={1} gap={0.5}>
                <Box>
                  <Typography variant='body2' fontWeight='bold' component='span'>
                    {currentFileItem.file.name}
                  </Typography>
                  {splitFileItem && (
                    <Chip label={`Pages ${formattedPageRange}`} size='small' color='info' sx={{ ml: 1, verticalAlign: 'middle' }} />
                  )}
                </Box>
                <Typography variant='caption'>{formatBytes(currentFileItem.file.size)}</Typography>
                <Stack direction='row' my={1} spacing={1} alignItems='center'>
                  <TextField
                    label='Select Pages (recommended)'
                    value={pageRange}
                    onChange={e => handlePageRangeChange(e.target.value)}
                    placeholder='e.g. 1-3,5,7'
                    variant='standard'
                    size='small'
                    fullWidth
                    error={!isPageRangeValid}
                    helperText={
                      !isPageRangeValid
                        ? pdfPageCount
                          ? `Invalid page range. PDF has ${pdfPageCount} pages.`
                          : 'Invalid page range'
                        : pdfPageCount
                          ? `PDF has ${pdfPageCount} pages`
                          : 'Loading page count...'
                    }
                    disabled={currentFileItem.status !== FileUploadStatus.Idle || isSplittingPDF}
                  />
                  <Tooltip title='Specify pages to get better results. Example: 1-3,5,7'>
                    <HelpOutline fontSize='small' />
                  </Tooltip>
                </Stack>
                {renderStatus(currentFileItem, TypeOfFile.PayApp)}
              </Stack>

              <Tooltip title='Remove file'>
                <IconButton color='error' onClick={handleRemove} disabled={isSplittingPDF || isUploading}>
                  <Delete />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        )}
      </Stack>

      <PayappProjectSelectionDialog
        open={isProjectSelectionDialogOpen}
        onClose={() => setIsProjectSelectionDialogOpen(false)}
        onSelect={retreiveProjectDraws}
        isAdding={isRetrievingDraws}
      />
    </Box>
  );
};

export default PayAppUploadDialog;
